import { useState } from 'react';
import { Alert, ModalBody } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';

import useFetchScheduledMessageByUid from 'components/domain/automation/scheduledMessages/useFetchScheduledMessageByUid';
import useFetchSystemTemplate from 'components/domain/automation/systemTemplates/useFetchSystemTemplate';
import FormWithProvider from 'components/forms/form/Form';
import FormFieldset from 'components/forms/form/FormFieldset';
import LoadingPlaceholder from 'components/loader/LoadingPlaceholder';
import { getErrorMessage } from 'utils/axiosExceptionUtils';
import {
  getFormDefaultValues,
  scheduledMessagesEditModalSchema,
} from './ScheduledMessagesEditModal.schema';
import ScheduledMessagesEditModalFooter from './ScheduledMessagesEditModalFooter';
import ScheduledMessagesEditModalFormElements from './ScheduledMessagesEditModalFormElements';
import useScheduledMessagesEditModalFormSubmit from './useScheduledMessagesEditModalFormSubmit';

interface ScheduledMessagesEditModalProps {
  scheduledMessageUid: string;
  closeModal: () => void;
}

const ScheduledMessagesEditModal = ({
  scheduledMessageUid,
  closeModal,
}: ScheduledMessagesEditModalProps) => {
  const { t } = useTranslation();
  const [isOnPreviewMode, setIsOnPreviewMode] = useState(false);

  const {
    data: scheduledMessage,
    error,
    isLoading: isLoadingScheduledMessage,
    isError,
  } = useFetchScheduledMessageByUid({ scheduledMessageUid });

  const { onSubmit } = useScheduledMessagesEditModalFormSubmit({
    scheduledMessageUid,
    closeModal,
  });

  const shouldFetchTemplate = !!scheduledMessage?.template?.originalTemplateUid;
  const { data: template, isLoading: isLoadingTemplate } =
    useFetchSystemTemplate({
      systemTemplateUid: scheduledMessage?.template?.originalTemplateUid,
      enabled: shouldFetchTemplate,
    });

  if ((isLoadingTemplate && shouldFetchTemplate) || isLoadingScheduledMessage) {
    return <LoadingPlaceholder />;
  }

  if (isError && error) {
    return (
      <Alert bsStyle="danger">
        {getErrorMessage({
          apiErrorCode: error.response?.data.apiErrorCode,
          apiErrorMessage: error.response?.data?.apiErrorMessage ?? '',
          baseKey: 'pageAutomations.triggers.apiErrorCodes.',
          t,
        })}
      </Alert>
    );
  }

  return (
    <FormWithProvider
      defaultValues={getFormDefaultValues({ scheduledMessage, template })}
      onSubmit={onSubmit}
      resolver={scheduledMessagesEditModalSchema({
        messagingType: template?.messagingType,
      })}
      noValidate
      dirtyChecker
    >
      <FormFieldset>
        <ModalBody>
          <ScheduledMessagesEditModalFormElements
            isOnPreviewMode={isOnPreviewMode}
            scheduledMessage={scheduledMessage}
            setIsOnPreviewMode={setIsOnPreviewMode}
            template={template ?? null}
          />
        </ModalBody>
        <ScheduledMessagesEditModalFooter
          scheduledMessage={scheduledMessage}
          closeModal={closeModal}
        />
      </FormFieldset>
    </FormWithProvider>
  );
};

export default ScheduledMessagesEditModal;
